import { Button, Card } from "antd";
import { TableProps } from "antd/lib/table";
import { Table } from "components/base";
import { settingsIcon2 } from "svgs";
import { KeyValue } from "types";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store";
import { useEffect, useMemo } from "react";
import { getFullName } from "../../../../utils/helpers";
import { UserType } from "protobuf/user_service_pb";
import { getTotalVehicleMileage, getVehicle } from "store/actions";
import { useParams } from "react-router-dom";
import {VEHICLE_COLOR_CODE, VEHICLE_COLOR_TYPE} from "../../../../utils/mapped-data";
import "styles/components/tab-content/vehicles-content/model-info.scss";

const columns: TableProps<any>["columns"] = [
  {
    title: "label",
    dataIndex: "label",
    width: 180,
    ellipsis: true,
  },
  {
    title: "value",
    dataIndex: "value",
    render: ValueCell,
  },
];

function ValueCell(value: string, row: KeyValue, index: number) {
  switch (row.type) {
    case "color":
      return (
        <span
          className=""
          // style={
          //   {
          //     "--color-cell": value,
          //   } as React.CSSProperties
          // }
        >
          {value}
        </span>
      );
    case "last_maintaned":
      return (
        <span className="last-maintaned-cell">
          {value}
          <Button type="link" icon={settingsIcon2}>
            <span className="button-text">Schedule Maintenance</span>
          </Button>
        </span>
      );
    default:
      return value;
  }
}

type VehicleDetailParams = {
  vehicleId?: string;
};

export default function Detail() {
  const dispatch = useDispatch();
  const {
    vehicles: { selectedVehicle },
  } = useSelector<AppState, AppState>((state) => state);
  const { totalMileage } = useSelector<AppState, AppState["vehicles"]>(
    (state) => state.vehicles
  );

  const { vehicleId } = useParams<VehicleDetailParams>();

  useEffect(() => {
    if (!selectedVehicle && vehicleId != "") {
      dispatch(getVehicle(vehicleId || ""));
    }
    if (selectedVehicle) {
      dispatch(getTotalVehicleMileage({ device_id: selectedVehicle?.deviceIotId || "" }));
    }
  }, [dispatch, selectedVehicle]);

  const {
    common: { userType = 1 },
  } = useSelector<AppState, AppState>((state) => state);

  const initialValues = useMemo(() => {
    return {
      vehicle_id: selectedVehicle?.licensePlate || "",
      vehicle_model: selectedVehicle?.modelId || "",
      vehicle_model_name: selectedVehicle?.model || "",
      vehicle_owner_org_name: selectedVehicle?.ownerOrgName || "",
      vehicle_status: selectedVehicle?.status || "",
      client: selectedVehicle?.ownerOrgId || "",
      assign_to: selectedVehicle?.assignee?.map((u) => u.id) || [],
      zone: selectedVehicle?.zone_name,
      location: selectedVehicle?.locationName || "",
      iot_id: selectedVehicle?.deviceIotId || "",
      mileage: totalMileage || "0",
      functionalStatus: selectedVehicle?.vehicleFunctionalStatus || "OFF",
      transmission: selectedVehicle?.transmission || "Automatic",
      battery: selectedVehicle?.battery || "0%",
      assignedTo: selectedVehicle?.checkedOutByName || "",
      f_version: selectedVehicle?.firmwareVersion || "-",
      battery_soc_array: selectedVehicle?.batterySocItemsList || [],
      vehicle_expiry_date: selectedVehicle?.warrantyExpirationDate.split("T")[0] || "-",
    };
  }, [selectedVehicle, totalMileage]);

  console.log("selected Vehicle : ", selectedVehicle);

  const modelData =
    userType === 2
      ? [
          { label: "Model", value: initialValues.vehicle_model_name },
          { label: "Battery Health", value: "100%" },
          {
            label: "Required Tire Pressure",
            value:
              selectedVehicle?.model_type == 2
                ? "F - 30 PSI    R - 34 PSI"
                : "F - 35 PSI    R - 45 PSI",
          },
          { label: "Transmission", value: initialValues.transmission },
          {
            label: "Color",
            value: VEHICLE_COLOR_TYPE[selectedVehicle?.color || 1],
            type: "color",
          },
        ]
      : [
          { label: "Model", value: initialValues.vehicle_model_name },
          {
            label: "Required Tire Pressure",
            value:
              selectedVehicle?.model_type == 2
                ? "F - 30 PSI    R - 34 PSI"
                : "F - 35 PSI    R - 45 PSI",
          },
          { label: "Transmission", value: initialValues.transmission },
          {
            label: "Color",
            value: VEHICLE_COLOR_TYPE[selectedVehicle?.color || 1],
            type: "color",
          },
        ];

  const vehicleData = [
    { label: "VIN", value: selectedVehicle?.vin },
    { label: "Mileage", value: initialValues.mileage + " (KMs)" },
    ...(selectedVehicle?.model_type == 2 && initialValues.battery_soc_array.length > 0
      ? initialValues.battery_soc_array.map((battery, index) => {
          let socValue = "0.00%";
          try {
            const parsed = parseFloat(battery.soc);
            socValue = !isNaN(parsed) ? parsed.toFixed(2) + "%" : "0%";
          } catch (error) {
            console.error(`Error parsing battery SOC for battery ${index + 1}:`, error);
          }
          return {
            label: `Battery ${index + 1}`,
            value: socValue,
          };
        })
      : [
          {
            label: "Battery",
            value: initialValues.battery.includes("%")
              ? initialValues.battery
              : initialValues.battery + " %",
          },
        ]),
    {
      label: "Functional Status",
      value: selectedVehicle?.vehicleFunctionalStatus,
      type: "functional_status",
    },
    { label: "Zone", value: initialValues.zone },
    { label: "Assigned To", value: initialValues.assignedTo },
    { label: "Firmware Version", value: initialValues.f_version },
    { label: "Warranty Date", value: initialValues.vehicle_expiry_date },
  ];

  return (
    <section className="__detail-container">
      <div className="detail-spacer"></div>
      <div className="detail-content">
        <Card
          className="modern-card stats-card detail-card"
          bordered={false}
        >
          <div className="detail-table-wrapper">
            <Table
              title={() => "Model Information"}
              bordered={false}
              showHeader={false}
              customClasses="--simple-two-columns detail-table"
              columns={columns}
              dataSource={modelData}
              pagination={false}
            />
          </div>
          <div className="detail-table-wrapper">
            <Table
              title={() => "Vehicle Details"}
              bordered={false}
              showHeader={false}
              customClasses="--simple-two-columns detail-table"
              columns={columns}
              dataSource={vehicleData}
              pagination={false}
            />
          </div>
        </Card>
      </div>
    </section>
  );
}

function renderUtilizationStatus(value: number) {
  const status = value;
  switch (status) {
    case 0:
      return "In the Lot";
    case 1:
      return "Checked Out";
    case 2:
      return "In the Lot";
    case 3:
      return "In the Lot";
    case 4:
      return "In the Lot";
    case 5:
      return "In the Lot";
    default:
      return "Checked Out";
  }
}

function functionalStatusCell(value: number) {
  const status = value;
  switch (status) {
    case 0:
      return "Unknown";
    case 1:
      return "Ride In-progress";
    case 2:
      return "In Maintenance";
    case 3:
      return "Marked Broken";
    case 4:
      return "Idle";
    case 5:
      return "Retired";
    default:
      return "";
  }
}